import { Helmet } from 'react-helmet-async'
import { getHubPathForCategoryLabel } from '../data/tools'

const siteName = 'WebToolz365'
const siteUrl = 'https://webtoolz365.com'

function SEO(rawProps) {
  /** TOOL_SEO spreads may include `bodyArticle` (consumed by ToolArticle), not meta tags */
  const { bodyArticle: _omitBodyArticle, ...props } = rawProps
  const {
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = '/og-image.jpg',
  toolName,
  toolCategory,
  publishedTime,
  modifiedTime,
  author = 'WebToolz365',
  type = 'website',
  faqItems,
  featureList,
  /** 'WebPage' for category hubs; default for tools emits WebApplication + SoftwareApplication */
  schemaPageType,
  /** { name, url } url may be `/path` or absolute — for ItemList on hubs */
  itemListElements,
  /** 404 and other non-canonical pages: keep out of the index, still follow links */
  noindex = false,
} = props
  const fullTitle = title || `${siteName} - 66+ Free Online Tools`
  const canonical = canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl

  const enhancedDescription =
    description ||
    `Free professional ${toolName || 'online tools'} available 24/7. ${toolCategory ? `Best ${toolCategory} tools.` : ''} No signup required. 100% private and secure. Used by developers worldwide.`

  const seoKeywords =
    keywords ||
    `${toolName}, online ${toolName}, free ${toolName}, ${toolName} tool, web tools, developer tools, ${toolCategory}, online utilities`

  const categoryHubPath = toolCategory ? getHubPathForCategoryLabel(toolCategory) : null
  const categoryCrumbUrl = categoryHubPath ? `${siteUrl}${categoryHubPath}` : null

  const publisherOrg = {
    '@type': 'Organization',
    name: siteName,
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${siteUrl}/logo.svg`,
    },
  }

  const schemaOrgWebPage = (() => {
    if (type === 'article') {
      return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: fullTitle,
        description: enhancedDescription,
        url: canonical,
        inLanguage: 'en-US',
        author: { '@type': 'Organization', name: siteName, url: siteUrl },
        publisher: publisherOrg,
        datePublished: publishedTime || '2024-01-01',
        ...(modifiedTime ? { dateModified: modifiedTime } : {}),
      }
    }
    if (schemaPageType === 'WebPage') {
      return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: fullTitle,
        description: enhancedDescription,
        url: canonical,
        inLanguage: 'en-US',
        isPartOf: { '@type': 'WebSite', name: siteName, url: siteUrl },
        publisher: publisherOrg,
        ...(modifiedTime ? { dateModified: modifiedTime } : {}),
      }
    }
    return {
      '@context': 'https://schema.org',
      '@type': ['WebApplication', 'SoftwareApplication'],
      name: fullTitle,
      description: enhancedDescription,
      url: canonical,
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Any',
      browserRequirements: 'Requires JavaScript. Requires HTML5.',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      author: {
        '@type': 'Organization',
        name: siteName,
        url: siteUrl,
      },
      publisher: publisherOrg,
      datePublished: publishedTime || '2024-01-01',
      ...(modifiedTime ? { dateModified: modifiedTime } : {}),
      inLanguage: 'en-US',
      ...(featureList?.length ? { featureList } : {}),
    }
  })()

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      ...(toolCategory && categoryCrumbUrl
        ? [
            {
              '@type': 'ListItem',
              position: 2,
              name: toolCategory,
              item: categoryCrumbUrl,
            },
          ]
        : []),
      ...(toolName
        ? [
            {
              '@type': 'ListItem',
              position: toolCategory && categoryCrumbUrl ? 3 : 2,
              name: toolName,
              item: canonical,
            },
          ]
        : []),
    ],
  }

  /**
   * FAQPage schema is emitted only from authored `faqItems`, which ToolArticle and
   * CategoryHub render visibly on the page. Never synthesize Q&As here — schema for
   * text that appears nowhere on the page is a structured-data policy violation.
   */
  const customFaq =
    faqItems?.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.acceptedAnswer,
      },
    })) || []

  const faqSchema =
    toolName && customFaq.length
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: customFaq,
        }
      : null

  const itemListSchema =
    itemListElements?.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: fullTitle,
          description: enhancedDescription,
          numberOfItems: itemListElements.length,
          itemListElement: itemListElements.map((item, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: item.name,
            item: item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`,
          })),
        }
      : null

  return (
    <Helmet htmlAttributes={{ lang: 'en' }}>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={enhancedDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={canonical} />

      <meta
        name="robots"
        content={
          noindex
            ? 'noindex, follow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        }
      />
      <meta name="googlebot" content={noindex ? 'noindex, follow' : 'index, follow'} />
      <meta name="bingbot" content={noindex ? 'noindex, follow' : 'index, follow'} />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={enhancedDescription} />
      <meta property="og:image" content={`${siteUrl}${ogImage}`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={enhancedDescription} />
      <meta name="twitter:image" content={`${siteUrl}${ogImage}`} />
      <meta name="twitter:creator" content="@webtoolz365" />
      <meta name="twitter:site" content="@webtoolz365" />

      <meta name="category" content={toolCategory || 'Web Tools'} />

      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteName} />

      <script type="application/ld+json">{JSON.stringify(schemaOrgWebPage)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
      {itemListSchema && (
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      )}

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: siteName,
          url: siteUrl,
          logo: `${siteUrl}/logo.svg`,
          description:
            'Professional online tools available 24/7/365. Free, fast, and completely private.',
          sameAs: ['https://github.com/Faizgeeky/WebToolsz', 'https://github.com/Faizgeeky'],
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'Customer Support',
            url: siteUrl,
          },
        })}
      </script>
    </Helmet>
  )
}

export default SEO
